import { Button } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import Popover from 'react-bootstrap/Popover';
import { useTranslation } from 'react-i18next';
import { Field, Utils } from '../Utils';
import { DataTypesList } from '../datatype-list/DataTypesList';
import './FieldElementContainer.scss';

interface FieldElemContainerInterface {
  field: Field,
  fields: Field[],
  parentField: Field | null,
  sensitiveFields?: string[],
}

const FieldElemContainer = ({ field, fields, parentField, sensitiveFields = [] }: FieldElemContainerInterface) => {
  const [isDatatypeBtnClick, setIsDatatypeBtnClick] = useState(false);
  const { t: translate } = useTranslation();
  /**
   * @method toggleChildren
   * @description Toggles the visibility of child elements based on the field ID.
   * @param e - The mouse event that triggered the toggle.
   * @param fieldId - The ID of the field.
   */
  const toggleChildren = (e: React.MouseEvent<HTMLButtonElement>, fieldId: string) => {
    const element = e.currentTarget?.firstChild as HTMLElement;
    if (element) {
      element.classList.toggle('bi-caret-down-fill');
      element.classList.toggle('bi-caret-right-fill');
      const selector = `#field-${fieldId} .child-field`;
      document.querySelector(selector)?.classList.toggle('hide-child-elem');
    }
  };
  /**
   * @method isChildFieldExists
   * @description Method will validate given field has any child or not
   * @param field
   * @returns Boolean
   */
  const isChildFieldExists = (field: Field) => {
    let isBlnChildFieldExists = false;
    if (Utils.checkTypes(field.type, 'record')) {
      if (typeof field.type === 'object' && !Array.isArray(field.type) && field.type.fields && field.type.fields.length > 0) {
        isBlnChildFieldExists = true;
      } else if (Array.isArray(field.type)) {
        const filteredObj = field.type.filter((item) => (typeof item === 'object' && !Array.isArray(item.type) && item.type === 'record' && item.fields && item.fields.length));
        if (filteredObj.length) {
          isBlnChildFieldExists = true;
        }
      }
    } else if (Utils.checkTypes(field.type, 'array')) {
      isBlnChildFieldExists = true;
    }

    return isBlnChildFieldExists;
  };

  /**
   * @method popover
   * @description Renders a popover component for a given field.
   * @param field - The field object.
   * @returns The popover component.
   */
  const popover = (field: Field) => (
    <Popover id="popover-basic" placement="right" className="data-list-popover">
      <Popover.Body className="data-list-popover-body">
        <DataTypesList
          field={field}
        />
      </Popover.Body>
    </Popover>
  );

  /**
   * @method renderSelectDataType
   * @description Determines whether to render the select data type component.
   * @returns {boolean} Returns true if the select data type component should be rendered, false otherwise.
   */
  const renderSelectDataType = () => {
    if (parentField && Utils.checkTypes(parentField.type, 'array')) {
      return false;
    }
    return true;
  };

useEffect(() => {
  // Process each field
  const processFields = (fields: any) => {
    fields.forEach((field: any) => {
      if (Array.isArray(field.type)) {
        field.type.forEach(processFieldItem);
      }
    });
  };

  // Process individual field items
  const processFieldItem = (item: any) => {
    if (typeof item === 'object') {
      if (item.fields) {
        processFields(item.fields);
      } else if (item.type === 'array' && item.items.isArray) {
        item.items.forEach(processFieldItem);
      }
    }
  };

  // Start processing if fields are valid
  if (Array.isArray(fields) && fields.length && fields[0].fields?.length) {
    processFields(fields[0].fields);
    }
  }, [field.type, field.namespace, field.name]);

  return (
    <>
      {
        ('namespace' in field)
        && (
          <div className="row-container">
            <div className="child-connector" />
            <input
              readOnly
              disabled
              type="text"
              defaultValue={field.namespace}
              name="namespace"
              placeholder={translate('service_validation_error.txt_namespace_place') ?? ''}
              data-testid="namespace"
            />
          </div>)
      }
      <div className="row-container">
        {
          (Utils.checkTypes(field.type, 'record') || Utils.checkTypes(field.type, 'array')) ? (
            <>
              <div className="child-connector-root" />
              {isChildFieldExists(field) ? (
                <Button variant="tertiary" aria-label="add-child" type="button" className="btn-default button-caret-container" onClick={(e) => toggleChildren(e, field.id)}>
                  <i className="bi bi-caret-down-fill" />
                </Button>
              ) : (
                <Button variant="tertiary" aria-label="add-child" type="button" className="btn-default button-caret-container button-caret-container-disabled">
                  <i className="bi bi-caret-right-fill" />
                </Button>)}
            </>
          ) : (
            <div className="child-connector" />
          )
        }
        <div>
          <input
            readOnly
            disabled
            type="text"
            value={field.name}
            name={`field-name-${field.id}`}
            placeholder={translate('service_validation_error.txt_field_name') ?? ''}
            data-testid="field-name"
          />
        </div>
        {renderSelectDataType() && !('namespace' in field) && (
          <>
            <Button variant="primary" className="select-data-type" onClick={() => setIsDatatypeBtnClick(!isDatatypeBtnClick)} data-testid="select-datatype-btn">{translate('schemaGeneratorText.dataType')}</Button>
            {sensitiveFields.some((sensitiveField) => sensitiveField === field.name || sensitiveField.endsWith(`.${field.name}`)) && (
              <span className="encrypted-badge" data-testid="encrypted-badge">Encrypted</span>
            )}
          </>
        )}
        <div>
          {isDatatypeBtnClick && popover(field)}
        </div>
      </div>
    </>
  );
};

export default FieldElemContainer;
